"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "../../lib/utils";

export interface ToastProps {
  message: string;
  isVisible: boolean;
  onClose?: () => void;
  duration?: number;
  className?: string;
}

export function Toast({
  message,
  isVisible,
  onClose,
  duration = 2000,
  className
}: ToastProps) {
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  React.useEffect(() => {
    if (isVisible && duration > 0) {
      console.log('Toast visible:', message, 'duration:', duration);
      const timer = setTimeout(() => {
        console.log('Toast hiding after duration');
        onClose?.();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [isVisible, duration, onClose, message]);


  if (!isVisible || !mounted) return null;

  const toastElement = (
    <div
      className={cn(
        "fixed bottom-4 left-1/2 z-[9999] -translate-x-1/2 transform",
        "rounded-lg bg-black px-4 py-2 text-sm text-white shadow-lg",
        "animate-in fade-in-0 slide-in-from-bottom-2 duration-200",
        className
      )}
      role="status"
      aria-live="polite"
      style={{ position: 'fixed', zIndex: 9999 }}
    >
      {message}
    </div>
  );

  return createPortal(toastElement, document.body);
}

export function useToast() {
  const [toast, setToast] = React.useState<{
    message: string;
    isVisible: boolean;
  }>({
    message: "",
    isVisible: false,
  });

  const showToast = React.useCallback((message: string) => {
    console.log('useToast.showToast called with:', message);
    setToast({ message, isVisible: true });
  }, []);

  const hideToast = React.useCallback(() => {
    console.log('useToast.hideToast called');
    setToast(prev => ({ ...prev, isVisible: false }));
  }, []);

  React.useEffect(() => {
    console.log('useToast state changed:', toast);
  }, [toast]);

  return {
    toast,
    showToast,
    hideToast,
  };
}